'use client';

import { CurrencyDollar, Handshake, MapPin, Target, TrendUp, Trophy } from '@phosphor-icons/react/dist/ssr';
import {
    AllysCTASection,
    AllysCompanyStory,
    AllysLocationsGrid,
    AllysPageHero,
    AllysStatsSection,
    AllysTeamSection,
    AllysValuesSection
} from '@routeroyal/theme-allys';

type Props = {
  locale: string;
  messages: Record<string, unknown>;
  basePath: string;
};

export function AboutContentAllys({ locale, messages, basePath }: Props) {
  const about = messages.about as Record<string, Record<string, string> | string>;
  
  // Helper to safely get nested string
  const get = (path: string, fallback = ''): string => {
    const keys = path.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = about;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return fallback;
    }
    return typeof value === 'string' ? value : fallback;
  };

  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <AllysPageHero
        title={get('hero.title', 'About Us')}
        subtitle={get('hero.subtitle', 'Leading property developers in Mauritius')}
        backgroundImage="https://media.routeroyal.com/nvD8tECYdnDOy5JfA3scO17yNu9ce0Sm4TYTryhxGww/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvMDIzZjc4YjU4MDJiMGNjYzQ5OWQ1NjI0YTQ3ODAzYzEyZWU1YjM3OTBiNzAzZDJhMmZhM2VjNjk3Zjc2NTliZS5wbmc.png"
        size="medium"
      />
        
      {/* Company Story */}
      <AllysCompanyStory
        title={get('story.title', 'Our Story')}
        content={[
          get('story.paragraph1', ''),
          get('story.paragraph2', ''),
        ]}
        image="https://media.routeroyal.com/qh4I4E5HcFbwLfyS8Z93PbgsKzGHP_BZFXnf0PZ9sbc/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvMTNmNWUxYzFmNDlhMzNlOGQzNTQ1ZDYzNDk2NGE4ZTg1NzNmMDEwZjU0MWJkY2M2YTk4Yzk0NmI3NGFhM2YyMy53ZWJw.webp"
        imagePosition="right"
        background="white"
      />
        
      {/* Values Section */}
      <AllysValuesSection
        title={get('values.title', 'Our Values')}
        subtitle={get('values.subtitle', 'What drives us forward')}
        values={[
          {
            title: get('values.quality.title', 'Quality'),
            description: get('values.quality.description', ''),
            icon: <Trophy size={40} weight="light" className="text-(--color-accent) opacity-90" />,
          },
          {
            title: get('values.location.title', 'Location'),
            description: get('values.location.description', ''),
            icon: <MapPin size={40} weight="light" className="text-(--color-accent) opacity-90" />,
          },
          {
            title: get('values.affordability.title', 'Affordability'),
            description: get('values.affordability.description', ''),
            icon: <CurrencyDollar size={40} weight="light" className="text-(--color-accent) opacity-90" />,
          },
          {
            title: get('values.expertise.title', 'Expertise'),
            description: get('values.expertise.description', ''),
            icon: <Target size={40} weight="light" className="text-(--color-accent) opacity-90" />,
          },
          {
            title: get('values.service.title', 'Service'),
            description: get('values.service.description', ''),
            icon: <Handshake size={40} weight="light" className="text-(--color-accent) opacity-90" />,
          },
          {
            title: get('values.investment.title', 'Investment'),
            description: get('values.investment.description', ''),
            icon: <TrendUp size={40} weight="light" className="text-(--color-accent) opacity-90" />,
          },
        ]}
        background="gray"
        columns={3}
      />
        
      {/* Team Section */}
      <AllysTeamSection
        title={get('team.title', 'Our Team')}
        subtitle={get('team.subtitle', 'Meet our leadership')}
        members={[
          {
            name: 'Mr. Bilal Mamodeally',
            role: get('team.managingDirector.role', 'Managing Director'),
            bio: get('team.managingDirector.bio', ''),
            image: 'https://media.routeroyal.com/rd5qRx6CyMteS7bmdNgKnmEKIS7V6JhZzq1IFQNUr5g/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvY2Y5MWM5ODIyOTBiNDg4NmJjMmVlOWZkNTI1NDM5OTQzNzRlODNjNDY3M2QxMDZlOWRlMzkxYmZkYmUzODA4Zi53ZWJw.webp',
            social: {
              linkedin: '#',
              email: 'info@allyandallys.com',
            },
          },
        ]}
        layout="featured"
      />
        
      {/* Locations We Serve - links to dedicated area pages for SEO */}
      <AllysLocationsGrid
        title={get('locations.title', 'Where We Serve')}
        subtitle={get('locations.subtitle', 'Find us across the island')}
        locations={[
          {
            name: 'Flic en Flac',
            description: get('locations.flicEnFlac', 'Coastal paradise'),
            image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=300&fit=crop',
            link: `/${locale}/areas/flic-en-flac`,
          },
          {
            name: 'Grand Baie',
            description: get('locations.grandBay', 'Premium beachfront'),
            image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=400&h=300&fit=crop',
            link: `/${locale}/areas/grand-baie`,
          },
          {
            name: 'Pereybere',
            description: get('locations.pereybere', 'Vibrant north coast'),
            image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=300&fit=crop',
            link: `/${locale}/areas/pereybere`,
          },
          {
            name: 'Tamarin',
            description: get('locations.tamarin', 'Surf capital'),
            image: 'https://images.unsplash.com/photo-1512100356356-de1b84283e18?w=400&h=300&fit=crop',
            link: `/${locale}/areas/tamarin`,
          },
          {
            name: 'Belle Mare',
            description: get('locations.belleMare', 'East coast paradise'),
            image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop',
            link: `/${locale}/areas/belle-mare`,
          },
          {
            name: 'Bel Ombre',
            description: get('locations.belOmbre', 'Heritage estate'),
            image: 'https://images.unsplash.com/photo-1449844908441-8829872d2607?w=400&h=300&fit=crop',
            link: `/${locale}/areas/bel-ombre`,
          },
          {
            name: 'Black River',
            description: get('locations.blackRiver', 'Scenic west coast'),
            image: 'https://images.unsplash.com/photo-1499346030926-9a72daac6c63?w=400&h=300&fit=crop',
            link: `/${locale}/areas/black-river`,
          },
          {
            name: 'Le Morne',
            description: get('locations.leMorne', 'UNESCO heritage'),
            image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=300&fit=crop',
            link: `/${locale}/areas/le-morne`,
          },
        ]}
        style="cards"
      />
        
      {/* Stats Section */}
      <AllysStatsSection
        stats={[
          {
            value: '30+',
            label: get('stats.yearsExperience', 'Years in Business'),
          },
          {
            value: '10+',
            label: get('stats.completedProjects', 'Projects Delivered'),
          },
          {
            value: '100+',
            label: get('stats.happyClients', 'Satisfied Clients'),
          },
          {
            value: '50+',
            label: get('stats.propertiesSold', 'Properties Sold'),
          },
        ]}
      />
        
      {/* CTA Section */}
      <AllysCTASection
        title={get('cta.title', 'Ready to Find Your Perfect Property?')}
        description={get('cta.description', 'Let us help you discover your dream home')}
        primaryButton={{
          text: get('cta.viewProperties', 'View Properties'),
          href: `${basePath}/properties`,
          variant: 'primary',
        }}
        secondaryButton={{
          text: get('cta.contactUs', 'Contact Us'),
          href: `${basePath}/contact`,
          variant: 'secondary',
        }}
        cardBackground="gradient"
      />
    </main>
  );
}
